import { useMemo } from "react";

import { useGetBookmarkCatalogQuery } from "apis/dataV2/dataV2Api";
import { useAppSelector } from "app/redux-store-interactions";
import { useSceneId } from "hooks/useSceneId";
import { selectUser } from "slices/authSlice";
import { selectIsOnline } from "slices/explorer";

export function useBookmarkCatalog({ skip }: { skip?: boolean } = {}) {
    const projectId = useSceneId();
    const user = useAppSelector(selectUser);
    const isOnline = useAppSelector(selectIsOnline);

    const publicScope = useGetBookmarkCatalogQuery({ projectId }, { skip });

    // Offline the personal scope is served from the local cache, so ask for it even when signed out.
    const personalScopeRequiresSignIn = !user && isOnline;
    const personalScope = useGetBookmarkCatalogQuery(
        { projectId, personal: true },
        { skip: skip || personalScopeRequiresSignIn },
    );

    const error = publicScope.error ?? personalScope.error;

    const catalog = useMemo(
        () => [...(publicScope.data ?? []), ...(personalScope.data ?? [])],
        [publicScope.data, personalScope.data],
    );

    return {
        catalog,
        isFetching: publicScope.isFetching || personalScope.isFetching,
        isError: Boolean(error),
        refetch: () => {
            if (!publicScope.isUninitialized) {
                publicScope.refetch();
            }
            if (!personalScope.isUninitialized) {
                personalScope.refetch();
            }
        },
    };
}
